import type {
  AdminPickerDetailDto,
  AdminPickerListDto,
  AdminPickerSourceFilter,
  AdminPickerStateFilter,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin «Подборщики» page (AUT-400 / CP-10a),
 * against the AUT-398 + AUT-400 backend:
 *
 *   - `GET  /admin/pickers`              (cursor-paginated, + `totals`)
 *   - `GET  /admin/pickers/:id`          (+ last activity, latest reports)
 *   - `POST /admin/pickers/:id/disable`  `{reason}`
 *   - `POST /admin/pickers/:id/enable`   `{reason}`
 *
 * Query keys: list → `['admin','pickers','list', params]`, detail →
 * `['admin','pickers','detail', id]`. Writes invalidate the prefix.
 *
 * Both writes answer the fresh DETAIL, so the caller patches the drawer in
 * place. `activeLinkCount` in that answer is usually stale after a disable
 * — the link sweep is an async listener — so the caller also refetches the
 * detail once a moment later (`usePickerAccessActions`).
 *
 * The writes pass `skipAutoErrorToast`: the caller maps the error `code`
 * to its own toast, so the shared 403 / 409 toast would double it.
 */

const PICKERS_BASE = '/admin/pickers';

export const ADMIN_PICKERS_KEY = ['admin', 'pickers'] as const;

export const pickerDetailKey = (id: string | null): readonly unknown[] => [
  ...ADMIN_PICKERS_KEY,
  'detail',
  id,
];

export interface AdminPickersListParams {
  state: AdminPickerStateFilter;
  source?: AdminPickerSourceFilter;
  cityCountry?: string;
  city?: string;
  q?: string;
  cursor: string | null;
  limit: number;
}

const buildListParams = (params: AdminPickersListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.state !== 'all') query.state = params.state;
  if (params.source) query.source = params.source;
  if (params.cityCountry) query.cityCountry = params.cityCountry;
  if (params.city) query.city = params.city;
  if (params.q) query.q = params.q;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminPickersList = (
  params: AdminPickersListParams,
): UseQueryResult<AdminPickerListDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_PICKERS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminPickerListDto>(PICKERS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminPickerDetail = (
  id: string | null,
): UseQueryResult<AdminPickerDetailDto, Error> =>
  useQuery({
    queryKey: pickerDetailKey(id),
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminPickerDetailDto>(`${PICKERS_BASE}/${id}`, {
        signal,
      });
      return data;
    },
  });

export type PickerAccessAction = 'disable' | 'enable';

/** `POST /admin/pickers/:id/{disable|enable}` — the reason is required. */
export const postPickerAccess = async (
  id: string,
  action: PickerAccessAction,
  reason: string,
): Promise<AdminPickerDetailDto> => {
  const { data } = await apiClient.post<AdminPickerDetailDto>(
    `${PICKERS_BASE}/${id}/${action}`,
    { reason },
    { skipAutoErrorToast: true },
  );
  return data;
};

export const useAdminPickersCache = (): {
  patchDetail: (id: string, detail: AdminPickerDetailDto) => void;
  invalidate: () => Promise<void>;
  refetchDetail: (id: string) => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    patchDetail: (id, detail) => {
      queryClient.setQueryData(pickerDetailKey(id), detail);
    },
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_PICKERS_KEY }),
    refetchDetail: (id) => queryClient.invalidateQueries({ queryKey: pickerDetailKey(id) }),
  };
};
